<template>
  <section>
    <scroll-container
      :class="[ isInEditMode ? 'html-ad-fs--edit-mode' :'html-ad-fs', 'pt-2 mt-n30px']"
      height-offset="isInEditMode? '55px' : '125px'"
    >
      <b-row v-if="loading">
        <b-col
          sm="12"
          class="text-center"
        >
          <b-skeleton
            class="d-inline-block"
            animation="wave"
            width="40%"
            height="300px"
          />
        </b-col>
      </b-row>
      <b-container
        v-else
        class="container-small"
      >
        <b-card class="mb-2">
          <div class="mb-2">
            <b-card-title
              v-if="!isInEditMode"
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.NewDisplayAdHTML') }}
            </b-card-title>
            <b-card-title
              v-else
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.EditDisplayAdHTML') }}
            </b-card-title>
            <b-breadcrumb class="m-0 p-0">
              <b-breadcrumb-item :to="`/${$route.params.accountId}/campaign/edit/${wrapper.campaignModel.id}`">
                {{ wrapper.campaignModel.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="`/${$route.params.accountId}/adgroup/edit?adGroupId=${wrapper.adGroup.id}&campaignId=${wrapper.campaignModel.id}`">
                {{ wrapper.adGroup.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-if="!isInEditMode"
                active
              >
                {{ $tk('ClientPortal.Form.NewAd') }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-else
                active
              >
                {{ wrapper.displayAdEdit.adName }}
              </b-breadcrumb-item>
            </b-breadcrumb>
            <hr class="m-0 mt-1">
          </div>
          <b-form-group
            class="mb-0"
            :label="$tk('ClientPortal.Form.AdCreation.SelectFiles')"
            label-size="sm"
            label-class="pb-50"
          >
            <vue-dropzone
              id="dropzone"
              ref="htmlDropzone"
              :use-custom-slot="true"
              :options="dropzoneOptions"
              @vdropzone-success="uploadFile"
              @vdropzone-processing="dropzoneChangeUrl"
              @vdropzone-error="errorHandler"
              @vdropzone-sending="sendFile"
              @vdropzone-complete="removeFile"
            >
              <div class="dz-default dz-message dz-message-center mx-3">
                <span class="d-block mb-3 line-height-1">
                  {{ $tk('ClientPortal.Form.UploadFilesWhichContainTheseFormats') }}
                  <span
                    v-for="item in wrapper.adFormats"
                    :key="item.id"
                    class="format-type"
                  >{{ item.name }}
                  </span>
                <!-- 300x250 170x600 728x90 300x600 970x250 800x400 705x230 320x40 300x300 -->
                </span>
                <div>
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-50 mb-50 mb-sm-0"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Hyperlink"
                    />
                    {{ $tk('ClientPortal.Form.UploadFiles') }}
                  </b-button>

                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="showGallery($event)"
                  >
                    <GjIcon
                      class="mr-50"
                      name="Image"
                    />
                    {{ $tk('ClientPortal.Form.ChooseFromCreatives') }}
                  </b-button>
                </div>
              </div>
            </vue-dropzone>
            <span
              v-if="uploadMessage"
              :class="(uploadMessage.results.length) !== 0 ? 'text-success' : 'text-danger'"
              class="field-validation-valid small"
            >
              {{ uploadMessage.message }}
            </span>
            <span
              v-else-if="wrongFormat"
              class="field-validation-valid small text-danger"
            >
              {{ wrongFormat }}
            </span>
          </b-form-group>
        </b-card>

        <!-- AI HTML Ad Generation Section -->
        <template v-if="configuration.openAiApiKeyConfiguration.useOpenAiApiKey">
          <b-card class="mb-2">
            <b-button
              v-b-toggle="'ai-generation-section'"
              class="bg-white w-100 text-left"
              variant="outline-primary"
              size="sm"
            >
              <GjIcon
                class="mr-50"
                name="Grid"
              />
              <span class="when-open">
                {{ $tk('ClientPortal.Form.AI.HideAIGeneration') || 'Hide AI Generation' }}
                <GjIcon
                  class="ml-50"
                  name="ArrowUp"
                />
              </span>
              <span class="when-closed">
                {{ $tk('ClientPortal.Form.AI.ShowAIGeneration') || 'Generate HTML Ad with AI' }}
                <GjIcon
                  class="ml-50"
                  name="ArrowDown"
                />
              </span>
            </b-button>

            <b-collapse
              id="ai-generation-section"
              class="mt-3"
            >
              <div>
                <!-- Prompt Section - Full Width -->
                <b-form-group
                  :label="$tk('ClientPortal.Form.AI.Prompt') || 'Describe your ad'"
                  label-size="sm"
                  class="mb-3"
                >
                  <b-form-textarea
                    v-model="aiGeneration.prompt"
                    :placeholder="$tk('ClientPortal.Form.AI.PromptPlaceholder') || 'Describe the ad you want to create (e.g., modern tech company banner with blue theme)'"
                    rows="4"
                    required
                  />
                </b-form-group>

                <!-- Format and Logo Section - Side by Side -->
                <b-row class="mb-1">
                  <b-col md="6">
                    <b-form-group
                      :label="$tk('ClientPortal.Form.AI.AdFormat') || 'Ad Format'"
                      label-size="sm"
                      class="mb-3"
                    >
                      <b-form-select
                        v-model="aiGeneration.selectedFormat"
                        :options="aiFormatOptions"
                        required
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group class="mb-3">
                      <label class="form-label">{{ $tk('ClientPortal.Form.AI.LogoUrl') || 'Logo URL (Optional)' }}</label>
                      <b-form-input
                        v-model="aiGeneration.logoUrl"
                        type="url"
                        :placeholder="$tk('ClientPortal.Form.AI.LogoUrlPlaceholder') || 'https://example.com/logo.png'"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Generate Button -->
                <div class="text-center mb-4">
                  <b-button
                    variant="primary"
                    size="lg"
                    :disabled="aiGeneration.generating || isPromptInvalid || !aiGeneration.selectedFormat || !wrapper.adFormats || !wrapper.adFormats.length"
                    @click="generateAIAd"
                  >
                    <b-spinner
                      v-if="aiGeneration.generating"
                      small
                      class="mr-2"
                    />
                    <GjIcon
                      v-else
                      class="mr-50"
                      name="Grid"
                    />
                    {{ aiGeneration.generating ? ($tk('ClientPortal.Form.AI.Generating') || 'Generating...') : ($tk('ClientPortal.Form.AI.GenerateAd') || 'Generate Ad') }}
                  </b-button>
                </div>

                <!-- Preview Section - Full Width -->
                <div v-if="aiGeneration.generatedHtml || aiGeneration.generating">
                  <b-card class="preview-image-ad">
                    <b-card-title
                      v-b-toggle="'ai-preview-collapse'"
                      class="d-flex justify-content-between p-2 mb-0"
                      @click="showAIGeneratedAd"
                    >
                      <div class="text-primary d-flex align-items-center">
                        <GjIcon name="Show" />
                        {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
                      </div>
                      <span class="when-open">
                        <GjIcon name="ArrowUp" />
                      </span>
                      <span class="when-closed">
                        <GjIcon name="ArrowDown" />
                      </span>
                    </b-card-title>
                    <b-collapse
                      id="ai-preview-collapse"
                      visible
                    >
                      <div class="card-body bg-white text-center mb-1">
                        <div
                          v-if="!aiGeneration.generatedHtml"
                          class="d-flex align-items-center justify-content-center text-muted"
                          style="min-height: 250px;"
                        >
                          <div class="text-center">
                            <b-spinner class="mb-3" />
                            <div>{{ $tk('ClientPortal.Form.AI.GeneratingPreview') || 'Generating your AI ad...' }}</div>
                          </div>
                        </div>
                        <div v-else>
                          <div class="display-label">
                            {{ $tk('ClientPortal.Form.AdPreview.DestinationUrl') }}: {{ aiGeneration.destinationUrl || 'No URL specified' }}
                          </div>
                          <div
                            style="position: relative;"
                            :style="{ height: `${aiGeneration.previewHeight}px` }"
                          >
                            <a
                              :href="aiGeneration.destinationUrl"
                              target="_blank"
                              style="text-decoration: none;"
                            >
                              <div style="position:absolute; z-index: 500; width:100%;">
                                <iframe
                                  :key="aiGeneration.iframeKey"
                                  :srcdoc="aiGeneration.generatedHtml"
                                  :width="aiGeneration.previewWidth"
                                  :height="aiGeneration.previewHeight + 20"
                                  style="width: 100%; border: 0px;"
                                  class="px-2 display-ad-preview-html"
                                />
                              </div>
                            </a>
                          </div>

                          <!-- Action Buttons -->
                          <div class="d-flex justify-content-center gap-2 mt-3">
                            <b-button
                              variant="outline-primary"
                              size="sm"
                              @click="copyHtmlToClipboard"
                            >
                              <GjIcon
                                class="mr-50"
                                name="Copy"
                              />
                              {{ $tk('ClientPortal.Form.AI.CopyHTML') || 'Copy HTML' }}
                            </b-button>
                            <b-button
                              variant="outline-success"
                              size="sm"
                              @click="downloadAsZip"
                            >
                              <GjIcon
                                class="mr-50"
                                name="Download"
                              />
                              {{ $tk('ClientPortal.Form.AI.DownloadZIP') || 'Download .zip' }}
                            </b-button>
                            <b-button
                              variant="success"
                              size="sm"
                              @click="useGeneratedAd"
                            >
                              <GjIcon
                                class="mr-50"
                                name="Check"
                              />
                              {{ $tk('ClientPortal.Form.AI.UseThisAd') || 'Use This Ad' }}
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </b-collapse>
                  </b-card>
                </div>
              </div>
            </b-collapse>
          </b-card>
        </template>

        <div
          v-if="isInEditMode"
          class="mb-2"
        >
          <b-card
            class="mb-2"
          >
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.FileName')"
            >
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    v-model="wrapper.displayAdEdit.fileName"
                    type="text"
                    placeholder="300x250.jpg"
                    disabled
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.AdName') + ' *'"
              :invalid-feedback="wrapper.displayAdEdit.errors['AdName']"
              :state="wrapper.displayAdEdit.errors['AdName'] == null ? null : false"
            >
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    :id="'ad-name-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.adName"
                    type="text"
                    :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
                    :state="wrapper.displayAdEdit.errors['AdName'] == null ? null : false"
                  />
                </b-col>
                <b-col
                  class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'name')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Form.Url') + ' *'"
              :invalid-feedback="wrapper.displayAdEdit.errors['Url']"
              :state="wrapper.displayAdEdit.errors['Url'] == null ? null : false"
            >
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    :id="'ad-url-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.url"
                    type="text"
                    placeholder="https://www.example.com/page"
                    :state="wrapper.displayAdEdit.errors['Url'] == null ? null : false"
                  />
                </b-col>
                <b-col
                  class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'url')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
            <b-button
              v-b-toggle="'url-builder'"
              class="bg-white mb-2"
              size="sm"
              variant="outline-primary"
            >
              <GjIcon
                class="mr-50"
                name="Hyperlink"
              />
              <span class="when-open">
                {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
                <GjIcon
                  class="mr-50"
                  name="ArrowUp"
                />
              </span>
              <span class="when-closed">
                {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
                <GjIcon
                  class="mr-50"
                  name="ArrowDown"
                />
              </span>
            </b-button>
            <b-collapse
              :id="'url-builder'"
            >
              <url-builder
                :ref="'utm'"
                v-model="wrapper.displayAdEdit.url"
                :options="wrapper.utmOptions"
              />
            </b-collapse>
            <b-form-group
              class="mb-0"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.ImpressionTag') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-row>
                <b-col sm="11">
                  <b-form-input
                    :id="'ad-imp-'+wrapper.displayAdEdit.id"
                    v-model="wrapper.displayAdEdit.impressionTag"
                    type="text"
                    :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
                  />
                </b-col>
                <b-col
                  class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                  sm="1"
                  @click="copyText(wrapper.displayAdEdit.id,'imp')"
                >
                  <GjIcon
                    class="cursor-pointer"
                    name="Copy"
                  />
                </b-col>
              </b-form-row>
            </b-form-group>
          </b-card>
          <b-card class="preview-image-ad">
            <b-card-title
              v-b-toggle="'collapse-edit-ad-preview'"
              class="d-flex justify-content-between p-2 mb-0"
              @click="showDisplayAd(wrapper.displayAdEdit)"
            >
              <div
                class="text-primary d-flex align-items-center"
              >
                <GjIcon name="Show" />
                {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
              </div>
              <span
                class="when-open"
              >
                <GjIcon name="ArrowUp" />
              </span>
              <span class="when-closed">
                <GjIcon name="ArrowDown" />
              </span>
            </b-card-title>
            <b-collapse
              id="collapse-edit-ad-preview"
            >
              <div
                class="card-body bg-white text-center mb-1"
              >
                <div class="display-label">
                  {{ $tk('ClientPortal.Form.AdPreview.DestinationUrl') }}: {{ wrapper.displayAdEdit.url }}
                </div>
                <div
                  style="position: relative;"
                  :style="{ height: `${fileHeight}px` }"
                >
                  <a
                    href="wrapper.displayAdEdit.url"
                    target="_blank"
                  >
                    <iframe
                      class="px-2 w-100 h-100 display-ad-preview-html"
                      :src="wrapper.displayAdEdit.mediaUrl"

                      style="border: 0px;"
                    />
                  </a>
                </div>
              </div>
            </b-collapse>
          </b-card>
        </div>
        <template v-else>
          <div
            v-for="(displayAd, index) in wrapper.displayAds"
            :key="`display-ad-${displayAd.id || 'temp'}-${index}`"
            class="mb-2"
          >
            <b-card
              class="mb-2"
            >
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.FileName')"
              >
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      v-model="displayAd.fileName"
                      type="text"
                      placeholder="300x250.jpg"
                      disabled
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                  >
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeDisplayAd(displayAd.id)"
                    >
                      <GjIcon
                        class="cursor-pointer text-secondary"
                        name="Delete"
                      />
                    </b-button>
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.AdName') + ' *'"
                :invalid-feedback="displayAd.errors['AdName']"
                :state="displayAd.errors['AdName'] == null ? null : false"
              >
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      :id="'ad-name-'+displayAd.id"
                      v-model="displayAd.adName"
                      type="text"
                      :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
                      :state="displayAd.errors['AdName'] == null ? null : false"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'name')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-form-group
                class="mb-2"
                :label="$tk('ClientPortal.Form.Url') + ' *'"
                :invalid-feedback="displayAd.errors['Url']"
                :state="displayAd.errors['Url'] == null ? null : false"
              >
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      :id="'ad-url-'+displayAd.id"
                      v-model="displayAd.url"
                      type="text"
                      placeholder="https://www.example.com/page"
                      :state="displayAd.errors['Url'] == null ? null : false"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'url')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
              <b-button
                v-b-toggle="'url-builder-'+index"
                class="bg-white mb-2"
                size="sm"
                variant="outline-primary"
              >
                <GjIcon
                  class="mr-50"
                  name="Hyperlink"
                />
                <span class="when-open">
                  {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
                  <GjIcon
                    class="mr-50"
                    name="ArrowUp"
                  />
                </span>
                <span class="when-closed">
                  {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
                  <GjIcon
                    class="mr-50"
                    name="ArrowDown"
                  />
                </span>
              </b-button>
              <b-collapse
                :id="'url-builder-'+index"
              >
                <url-builder
                  :ref="'utm-'+index"
                  :key="index"
                  v-model="displayAd.url"
                  :options="wrapper.utmOptions"
                />
              </b-collapse>
              <b-form-group
                class="mb-0"
              >
                <legend class="col-form-label pt-0">
                  {{ $tk('ClientPortal.Form.ImpressionTag') }}
                  <GjIcon
                    v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                    name="Info"
                  />
                </legend>
                <b-form-row>
                  <b-col sm="11">
                    <b-form-input
                      :id="'ad-imp-'+displayAd.id"
                      v-model="displayAd.impressionTag"
                      type="text"
                      :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
                    />
                  </b-col>
                  <b-col
                    class="pt-1 pt-sm-50 d-flex flex-row-reverse flex-sm-row"
                    sm="1"
                    @click="copyText(displayAd.id,'imp')"
                  >
                    <GjIcon
                      class="cursor-pointer"
                      name="Copy"
                    />
                  </b-col>
                </b-form-row>
              </b-form-group>
            </b-card>
            <b-card class="preview-image-ad">
              <b-card-title
                v-b-toggle="`collapse-ad-preview-${displayAd.id}`"
                class="d-flex justify-content-between p-2 mb-0"
                @click="showDisplayAd(displayAd)"
              >
                <div
                  class="text-primary d-flex align-items-center"
                >
                  <GjIcon name="Show" />
                  {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
                </div>
                <span
                  class="when-open"
                >
                  <GjIcon name="ArrowUp" />
                </span>
                <span class="when-closed">
                  <GjIcon name="ArrowDown" />
                </span>
              </b-card-title>
              <b-collapse
                :id="`collapse-ad-preview-${displayAd.id}`"
              >
                <div class="card-body bg-white text-center mb-1">
                  <div class="display-label">
                    {{ $tk('ClientPortal.Form.AdPreview.DestinationUrl') }}: {{ displayAd.url }}
                  </div>
                  <div
                    style="position: relative;"
                    :style="{ height: displayAd.fileHeight + 'px' }"
                  >
                    <a
                      href="displayAd.url"
                      target="_blank"
                    >
                      <div style="position:absolute; z-index: 500; width:100%;">
                        <iframe
                          class="px-2 display-ad-preview-html"
                          :src="displayAd.mediaUrl"
                          :height="displayAd.fileHeight+20"
                          :width="displayAd.fileWidth"
                          style="width: 100%; border: 0px;"
                        />
                      </div>
                    </a>
                  </div>
                </div>
              </b-collapse>
            </b-card>
          </div>
        </template>
      </b-container>
    </scroll-container>

    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          v-if="isInEditMode"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          @click="closeEditModal"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-else
          :to="`/${$route.params.accountId}`"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click="saveDisplayAd()"
        >
          {{ isInEditMode ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.CreateAd') }}
        </b-button>
      </div>
    </div>
    <b-modal
      id="gallery-modal"
      ref="galleryModal"
      body-class="modal-body--no-padding creatives-modal"
      hide-footer
      :title="$tk('ClientPortal.Form.Gallery')"
      size="xl"
      centered
    >
      <loader
        v-if="loadingGallery"
        :size="24"
        :show="true"
        class="gallery_loader"
      />
      <div v-else>
        <div class="d-block mx-1 mb-1">
          <b-form-input
            v-model="creativeSearchTerm"
            type="text"
            :placeholder="$tk('ClientPortal.Form.SearchCreative')"
            @input="searchCreative"
          />
        </div>
        <hr class="mb-0">
        <scroll-container
          class="pt-1 px-1"
          height-offset="260px"
        >
          <b-row class="mx-n50">
            <b-col
              v-for="(creativeItem, index) in creatives"
              :key="`creative-${creativeItem.id || 'temp'}-${index}`"
              xl="3"
              md="4"
              sm="6"
              cols="12"
              class="mb-1 px-50"
            >
              <b-card
                class="mb-0 h-100 border"
                :class="{'border-primary' : creativeItem.selected}"
                @click.prevent="selectCreative(creativeItem)"
              >
                <div class="text-right">
                  <b-form-radio
                    v-if="isInEditMode"
                    v-model="creativeItem.selected"
                    value="true"
                    name="creative"
                  />
                  <b-form-checkbox
                    v-else
                    v-model="creativeItem.selected"
                    class="d-inline-block pl-1"
                  />
                </div>
                <b-card
                  class="mb-2"
                  body-class="p-0"
                >
                  <iframe
                    :src="creativeItem.url"
                    class="scrollbar-sm"
                    style="width: 100%; height: 300px;"
                  />
                </b-card>
                <b-card-title class="mb-0">
                  {{ creativeItem.name }}
                </b-card-title>
              </b-card>
            </b-col>
            <span
              v-if="creatives.length === 0"
              class="gallery-text"
            >
              <div>
                <GjIcon
                  size="64px"
                  name="Grid"
                />
              </div>
              <div>
                <span v-if="creativeSearchTerm.length > 0"> {{ $tk('ClientPortal.CompanionAds.NoCreativesAreFoundWithThatName') }}</span>
                <span v-else> {{ $tk('ClientPortal.CompanionAds.NoCreativesFound') }}</span>
              </div>
            </span>
          </b-row>
          <InfiniteLoading
            v-if="creatives.length > 0"
            @infinite="loadCreatives($event, false)"
          >
            <div slot="no-results" />
            <div slot="no-more" />
          </InfiniteLoading>
        </scroll-container>
        <div class="p-1 border-top text-right">
          <b-button
            class=""
            variant="outline-secondary"
            size="sm"
            @click.prevent="hideGallery"
          >
            {{ $tk('ClientPortal.Form.Actions.Cancel') }}
          </b-button>
          <b-button
            class="ml-50"
            variant="primary"
            size="sm"
            @click.prevent="addCreativeItems()"
          >
            {{ $tk('ClientPortal.Form.Add') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import Vue from 'vue'
import { mapGetters } from 'vuex'
import notification from '@/mixins/toastification'
import sweetAlert from '@/mixins/sweetAlert'
import ScrollContainer from '@/components/ScrollContainer.vue'
import UrlBuilder from '@/components/UrlBuilder.vue'
import InfiniteLoading from 'vue-infinite-loading'
import getAPIDomain from '@/utils/apiDomain/apiDomain'
import AdsService from '@/api/client/ads.service'
import GalleryService from '@/api/client/gallery.service'
import loader from '@/components/Loader.vue'
import scrollToError from '@/mixins/scrollToError'

const HTML_CREATIVE_TYPE = 2
export default {
  inject: {
    closeEditModal: {
      from: 'closeEditModal',
      default: () => {},
    },
  },
  components: {
    GjIcon,
    vueDropzone: vue2Dropzone,
    ScrollContainer,
    UrlBuilder,
    InfiniteLoading,
    loader,
  },
  mixins: [notification, sweetAlert],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    adId: Number,
    // eslint-disable-next-line vue/require-default-prop
    adGroupId: Number,
    // eslint-disable-next-line vue/require-default-prop
    catalogSetId: {
      type: String,
    },
    saving: {
      type: Boolean,
    },
  },
  data() {
    return {
      wrapper: {},
      count: 1,
      uploadMessage: '',
      wrongFormat: null,
      dropzoneOptions: {
        uploadMultiple: true,
        parallelUploads: 5,
        acceptedFiles: '.rar,.zip',
        url: 'url',
        thumbnailWidth: null,
        timeout: 60000,
        thumbnailHeight: null,
        addRemoveLinks: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      },
      creatives: [],
      creativePage: 0,
      creativeSearchTerm: '',
      loading: false,
      loadingGallery: false,
      aiGeneration: {
        prompt: '',
        selectedFormat: null,
        logoUrl: '',
        generatedHtml: null,
        backgroundImageUrl: null,
        previewWidth: 300,
        previewHeight: 250,
        generating: false,
        iframeKey: 0,
        destinationUrl: '',
      },
      aiFormatOptions: [],
    }
  },
  computed: {
    ...mapGetters(['configuration']),
    isPromptInvalid() {
      return !this.aiGeneration.prompt || this.aiGeneration.prompt.trim() === ''
    },
    isFormComplete() {
      if (this.isInEditMode) {
        const { displayAdEdit } = this.wrapper
        return displayAdEdit && displayAdEdit.adName && displayAdEdit.url && displayAdEdit.fileName
      }
      const { displayAds } = this.wrapper
      return displayAds && displayAds.length && displayAds.every(x => x.adName && x.url && x.fileName)
    },
    isInEditMode() {
      return this.adId > 0
    },
    fileHeight() {
      if (!this.wrapper?.adFormats || !this.wrapper?.displayAdEdit) {
        return 250 // Return a default height
      }

      const format = this.wrapper.adFormats.find(x => x.id === this.wrapper.displayAdEdit.formatId)

      // If a format is found, return its height, otherwise return the default
      return format ? format.height : 250
    },
  },
  watch: {
    'wrapper.displayAdEdit.url': {
      handler(newUrl) {
        // Update AI preview destination URL when form URL changes
        if (this.aiGeneration.generatedHtml) {
          this.aiGeneration.destinationUrl = newUrl || ''
        }
      },
    },
  },
  async mounted() {
    this.loading = true
    try {
      this.wrapper = await AdsService.getAd(this.adId, this.adGroupId, this.catalogSetId, this.$route.params.accountId)
      if (this.wrapper && this.wrapper.adFormats) {
        this.aiFormatOptions = this.wrapper.adFormats.map(format => ({
          value: format.id,
          text: format.name,
        }))
      }
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    setSaving(value) {
      this.$emit('onUpdate', value)
    },
    dropzoneChangeUrl() {
      const clientApi = getAPIDomain()
      const { accountId } = this.$route.params
      this.$refs.htmlDropzone.setOption('url', `${clientApi}/upload/html?accountId=${accountId}`)
    },
    /* eslint-disable-next-line */
    errorHandler(file, message, xhr) {
      this.wrongFormat = message
    },
    async showDisplayAd(displayAd) {
      if (displayAd.show) {
        displayAd.show = false
      } else {
        displayAd.show = true
        try {
          const response = await AdsService.sanitazeUrl(displayAd.url)
          displayAd.url = response
        } catch (error) {
          this.error(error.message)
        }
      }
    },
    removeDisplayAd(id) {
      const index = this.wrapper.displayAds.findIndex(x => x.id === id)
      if (index > -1) {
        this.wrapper.displayAds.splice(index, 1)
      }
    },
    copyText(id, type) {
      var element = `ad-${type}-${id}`
      var copyText = document.getElementById(element)
      copyText.select()
      copyText.setSelectionRange(0, 99999)
      document.execCommand('copy')
    },
    async saveDisplayAd() {
      this.setSaving(true)

      if (this.isInEditMode) {
        const { entity } = this.$parent.$parent.$parent
        this.escapeImpressionTag([this.wrapper.displayAdEdit])
        try {
          const response = await AdsService.editDisplayAd(this.wrapper.displayAdEdit, this.$route.params.accountId)
          if (response.updated) {
            entity.name = this.wrapper.displayAdEdit.adName
            this.success(response.message)
            this.closeEditModal()
          } else {
            this.error(response.message)
            this.wrapper.displayAdEdit = response.displayAdModel
            this.$nextTick(() => {
              scrollToError(this.wrapper.displayAdEdit.errors)
            })
          }
        } catch (error) {
          this.error(error.message)
        } finally {
          this.setSaving(false)
        }
      } else if (this.wrapper.displayAds.length > 0) {
        this.escapeImpressionTag(this.wrapper.displayAds)
        try {
          const response = await AdsService.createDisplayAd(HTML_CREATIVE_TYPE, this.wrapper.displayAds, this.$route.params.accountId)
          if (response.saved) {
            this.wrapper.displayAds = []
            this.sweetAlertAfterAdCreation()
          } else {
            this.wrapper.displayAds = response.displayAds
            this.error(this.$tk('ClientPortal.Ads.ErrorSavingChanges'))
            this.$nextTick(() => {
              const firstInvalidInput = document.querySelector('.is-invalid')
              if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            })
            this.setSaving(false)
          }
        } catch (error) {
          this.error(error.message)
          this.setSaving(false)
        }
      }
    },
    escapeImpressionTag(displayAds) {
      displayAds.forEach(element => {
        element.impressionTag = encodeURIComponent(element.impressionTag)
      })
    },
    sendFile(file, xhr, formData) {
      formData.append('file', file)
    },
    uploadFile(file, response) {
      this.uploadMessage = ''
      this.wrongFormat = null
      response.results.forEach(element => {
        if (this.isInEditMode) {
          this.wrapper.displayAdEdit.mediaUrl = element.mediaUrl
          this.wrapper.displayAdEdit.formatId = element.formatId
          this.wrapper.displayAdEdit.fileWidth = file.width
          this.wrapper.displayAdEdit.fileHeight = file.height
          this.wrapper.displayAdEdit.fileName = file.name
        } else {
          const index = this.wrapper.displayAds.findIndex(x => x.fileUrl === element.mediaUrl)
          if (index === -1) {
            this.wrapper.displayAds.push({
              id: this.count,
              adName: '',
              fileWidth: element.width,
              fileHeight: element.height,
              fileName: element.mediaFileName,
              mediaUrl: element.mediaUrl,
              url: '',
              formatId: element.mediaTypeId,
              adGroupId: this.wrapper.adGroup.id,
              impressionTag: '',
              show: false,
              errors: {},
            })
            // eslint-disable-next-line no-plusplus
            this.count++
          }
        }
      })

      if (!response.success) this.uploadMessage = response.message

      this.setSaving(false)
    },
    removeFile(file) {
      this.$refs.htmlDropzone.removeFile(file)
    },
    showGallery(event) {
      event.stopPropagation()
      this.loadCreatives(null, true)
      this.$refs.galleryModal.show()
    },
    hideGallery() {
      this.$refs.galleryModal.hide()
    },
    async loadCreatives($state, load = false) {
      if (load) { this.loadingGallery = true }
      const filterModel = {
        page: this.creativePage,
        type: HTML_CREATIVE_TYPE,
        searchTerm: this.creativeSearchTerm,
      }
      try {
        const response = await GalleryService.getCreatives(filterModel, this.$route.params.accountId)
        if (response.length) {
          this.creativePage += 1
          this.creatives.push(...response)
          if ($state) $state.loaded()
        } else if ($state) $state.complete()
      } catch (error) {
        this.error(error.message)
      }
      this.loadingGallery = false
    },
    searchCreative() {
      Vue.set(this, 'creatives', [])
      this.creativePage = 0
      this.loadCreatives()
    },
    selectCreative(creative) {
      if (this.isInEditMode) {
        this.creatives.forEach(x => { x.selected = false })
      }
      creative.selected = !creative.selected
    },
    addCreativeItems() {
      if (this.isInEditMode) {
        const selectedCreative = this.creatives.find(x => x.selected === true)
        this.wrapper.displayAdEdit.mediaUrl = selectedCreative.url
        this.wrapper.displayAdEdit.fileName = selectedCreative.name
        this.wrapper.displayAdEdit.formatId = selectedCreative.formatId
      } else {
        const selectedCreatives = this.creatives.filter(x => x.selected === true)

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < selectedCreatives.length; i++) {
          this.wrapper.displayAds.push({
            id: this.count,
            name: selectedCreatives[i].name,
            adName: '',
            fileWidth: selectedCreatives[i].width,
            fileHeight: selectedCreatives[i].height,
            fileName: selectedCreatives[i].name,
            mediaUrl: selectedCreatives[i].url,
            url: '',
            formatId: selectedCreatives[i].formatId,
            adGroupId: this.wrapper.adGroup.id,
            impressionTag: '',
            show: false,
            errors: {},
          })
          // eslint-disable-next-line no-plusplus
          this.count++
          selectedCreatives[i].selected = false
        }
      }

      this.hideGallery()
    },
    async generateAIAd() {
      if (!this.aiGeneration.prompt || !this.aiGeneration.selectedFormat) {
        this.error('Please describe your ad and select a format.')
        return
      }

      this.aiGeneration.generating = true
      this.aiGeneration.generatedHtml = null

      const payload = {
        prompt: this.aiGeneration.prompt,
        formatId: this.aiGeneration.selectedFormat,
        logoUrl: this.aiGeneration.logoUrl,
      }
      try {
        const response = await AdsService.generateAIAd(payload, this.$route.params.accountId)
        if (response.success) {
          this.aiGeneration.generatedHtml = response.html
          this.aiGeneration.backgroundImageUrl = response.backgroundImageUrl
          this.aiGeneration.previewWidth = response.width
          this.aiGeneration.previewHeight = response.height
          this.aiGeneration.destinationUrl = this.wrapper.displayAdEdit.url || ''
          this.aiGeneration.iframeKey = Date.now()
          this.success(response.message || 'AI ad generated successfully!')
        } else {
          this.error(response.message || 'An unknown error occurred during ad generation.')
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message
        this.error(`Failed to generate ad: ${errorMessage}`)
      } finally {
        this.aiGeneration.generating = false
      }
    },

    async copyHtmlToClipboard() {
      try {
        const htmlContent = this.aiGeneration.generatedHtml
        await navigator.clipboard.writeText(htmlContent)
        this.success(this.$tk('ClientPortal.Form.AI.HTMLCopied') || 'HTML copied to clipboard')
      } catch (error) {
        const textArea = document.createElement('textarea')
        textArea.value = this.aiGeneration.generatedHtml
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.success(this.$tk('ClientPortal.Form.AI.HTMLCopied') || 'HTML copied to clipboard')
      }
    },
    async downloadAsZip() {
      try {
        const htmlContent = this.aiGeneration.generatedHtml

        if (!htmlContent) {
          this.error('No generated ad content is available to download.')
          return
        }

        const zipFile = await this.createHtmlZipFile(htmlContent)

        const url = window.URL.createObjectURL(zipFile)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', zipFile.name)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)

        this.success(this.$tk('ClientPortal.Form.AI.ZIPDownloaded') || 'ZIP file downloaded successfully')
      } catch (error) {
        console.error('Download error:', error)
        this.error(this.$tk('ClientPortal.Form.AI.DownloadFailed') || `Download failed: ${error.message}`)
      }
    },
    async useGeneratedAd() {
      if (!this.aiGeneration.generatedHtml) {
        this.error('No ad has been generated to use.')
        return
      }

      try {
        const zipFile = await this.createHtmlZipFile(this.aiGeneration.generatedHtml)
        const formData = new FormData()
        formData.append('file', zipFile)

        const result = await AdsService.uploadHtmlZip(formData, this.$route.params.accountId)

        let uploadResult
        if (result.success && result.results && result.results.length > 0) {
          const firstResult = result.results[0]
          uploadResult = {
            success: true,
            mediaUrl: firstResult.mediaUrl,
            fileName: firstResult.mediaFileName,
            formatId: firstResult.mediaTypeId,
            width: firstResult.width,
            height: firstResult.height,
            message: result.message,
          }
        } else {
          uploadResult = {
            success: false,
            message: result.message || 'Failed to upload HTML',
          }
        }

        if (!uploadResult.success) {
          this.error(uploadResult.message || 'Failed to upload HTML ad.')
          return
        }

        const newAdData = {
          fileWidth: uploadResult.width,
          fileHeight: uploadResult.height,
          fileName: uploadResult.fileName,
          mediaUrl: uploadResult.mediaUrl,
          formatId: uploadResult.formatId,
          url: '',
        }

        if (this.isInEditMode) {
          this.wrapper.displayAdEdit = { ...this.wrapper.displayAdEdit, ...newAdData }
        } else {
          const uniqueId = (this.wrapper.displayAds.reduce((max, ad) => Math.max(ad.id || 0, max), 0) || this.count) + 1
          this.wrapper.displayAds.push({
            id: uniqueId,
            adName: '',
            impressionTag: '',
            show: false,
            errors: {},
            ...newAdData,
            adGroupId: this.wrapper.adGroup.id,
          })
          this.count = uniqueId
        }

        this.success(this.$tk('ClientPortal.Form.AI.AdUsed') || 'Ad uploaded and added to the form. Please fill in the remaining details.')

        this.aiGeneration.generatedHtml = null
      } catch (error) {
        this.error(`Failed to upload HTML ad: ${error.message}`)
      }
    },
    async createHtmlZipFile(htmlContent) {
      try {
        const JSZip = (await import('jszip')).default

        const zip = new JSZip()
        const fileName = `ai_generated_ad_${Date.now()}.html`

        zip.file(fileName, htmlContent)

        const zipBlob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: {
            level: 6,
          },
        })

        // Create a File object from the ZIP blob
        const zipFileName = `ai_generated_ad_${Date.now()}.zip`
        return new File([zipBlob], zipFileName, { type: 'application/zip-compressed' })
      } catch (error) {
        console.error('Error creating HTML ZIP file:', error)
        throw new Error(`Failed to create ZIP file: ${error.message}`)
      }
    },
    showAIGeneratedAd() {
    },
  },
}
</script>

<!-- Create new scss file if you add more than two classes inside component -->
<style lang="scss">
@import '@/assets/scss/views/client/ads-manager/create/ads/html-ad.scss';
</style>
